<template>
  <div class="offer-form">
    <fieldset :disabled="isReadOnly" class="offer-fieldset">
      <b-row>
        <b-col lg="6" md="6" sm="12">
          <legend>
            <strong>{{ $t('offerDetails') }}</strong>
          </legend>

          <b-form-group
            horizontal
            :label="$t('offerType')"
            label-for="offerType"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <multiselect
              :disabled="isReadOnly"
              :placeholder="$t('offerType') + ' ' + $t('select')"
              v-model="offerType"
              :options="getEnumValues('OfferType')"
              :show-labels="true"
              :allow-empty="true"
              :selectLabel="$t('select')"
              :selectedLabel="$t('selected')"
              :deselectLabel="$t('remove_selection')"
            >
              <template slot="singleLabel" slot-scope="{ option }">
                <b-badge :variant="getOfferTypeColor(option)">{{ $t(option) }}</b-badge>
              </template>
              <template slot="option" slot-scope="{ option }">
                <b-badge :variant="getOfferTypeColor(option)">{{ $t(option) }}</b-badge>
              </template>
            </multiselect>
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('editing_state')"
            label-for="editingState"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <multiselect
              :disabled="isReadOnly"
              :placeholder="$t('editing_state') + ' ' + $t('select')"
              v-model="localOffer.editingState"
              :options="getEnumValues('OfferEditingState')"
              :show-labels="true"
              :allow-empty="false"
              :selectLabel="$t('select')"
              :selectedLabel="$t('selected')"
              :deselectLabel="$t('remove_selection')"
            >
              <template slot="singleLabel" slot-scope="{ option }">
                <b-badge :variant="getOfferEditingStateColor(option)">{{ $t(option) }}</b-badge>
              </template>
              <template slot="option" slot-scope="{ option }">
                <b-badge :variant="getOfferEditingStateColor(option)">{{ $t(option) }}</b-badge>
              </template>
            </multiselect>
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('customer_state')"
            label-for="customerState"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <multiselect
              :disabled="isReadOnly"
              :placeholder="$t('customer_state') + ' ' + $t('select')"
              v-model="localOffer.customerState"
              :options="getEnumValues('OfferCustomerState')"
              :show-labels="true"
              :allow-empty="false"
              :selectLabel="$t('select')"
              :selectedLabel="$t('selected')"
              :deselectLabel="$t('remove_selection')"
            >
              <template slot="singleLabel" slot-scope="{ option }">
                <b-badge :variant="getOfferCustomerStateColor(option)">{{ $t(option) }}</b-badge>
              </template>
              <template slot="option" slot-scope="{ option }">
                <b-badge :variant="getOfferCustomerStateColor(option)">{{ $t(option) }}</b-badge>
              </template>
            </multiselect>
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('offer_number')"
            label-for="offerNumber"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <b-form-input
              id="offerNumber"
              v-model="localOffer.offerNumber"
              type="text"
              :placeholder="$t('offer_number')"
            />
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('offerDate')"
            label-for="offerDate"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <Calendar
              v-model="offerDate"
              :disabled="isReadOnly"
              selectionMode="single"
              dateFormat="dd.mm.yy"
              :placeholder="$t('date')"
              :showWeek="true"
              :yearNavigator="true"
              :monthNavigator="true"
              yearRange="2020:2030"
            />
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('validUntil')"
            label-for="validUntil"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <Calendar
              v-model="validUntil"
              :disabled="isReadOnly"
              selectionMode="single"
              dateFormat="dd.mm.yy"
              :placeholder="$t('date')"
              :showWeek="true"
              :yearNavigator="true"
              :monthNavigator="true"
              yearRange="2020:2030"
            />
          </b-form-group>
        </b-col>

        <b-col lg="6" md="6" sm="12">
          <legend>
            <strong>{{ $t('amounts') }}</strong>
          </legend>

          <TaxAmountFields
            :amounts="offerAmounts"
            :isReadOnly="isReadOnly"
            @update:amounts="onAmountsChange"
          />
        </b-col>
      </b-row>

      <b-row>
        <b-col lg="6" md="6" sm="12">
          <legend>
            <strong>{{ $t('references') }}</strong>
          </legend>

          <b-form-group
            v-if="!projectContext"
            horizontal
            :label="$t('project')"
            label-for="project"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <MultiselectProjects v-model="localOffer.project" :disabled="isReadOnly" />
          </b-form-group>

          <b-form-group
            v-if="isIncoming"
            horizontal
            :label="$t('supplier')"
            label-for="supplier"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <MultiselectSuppliers v-model="localOffer.supplier" :disabled="isReadOnly" />
          </b-form-group>
        </b-col>

        <b-col lg="6" md="6" sm="12">
          <legend>
            <strong>{{ $t('additionalInfo') }}</strong>
          </legend>

          <b-form-group
            horizontal
            :label="$t('notes')"
            label-for="notes"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <b-form-textarea v-model="localOffer.notes" :placeholder="$t('notes')" rows="3" />
          </b-form-group>

          <b-form-group
            v-if="!isNew"
            horizontal
            :label="$t('number')"
            label-for="number"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
            class="mb-0"
          >
            <div class="form-control-plaintext">
              {{ localOffer.number.toString().padStart(4, '0') }}
            </div>
          </b-form-group>

          <b-form-group
            v-if="localOffer.createdBy"
            horizontal
            :label="$t('createdBy')"
            label-for="createdBy"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
            class="mb-0"
          >
            <div class="form-control-plaintext">
              {{ localOffer.createdBy.firstname }} {{ localOffer.createdBy.lastname
              }}<span v-if="localOffer.createdAt"
                >, {{ $dayjs(localOffer.createdAt).format('DD.MM.YYYY HH:mm') }}</span
              >
            </div>
          </b-form-group>

          <b-form-group
            v-if="localOffer.updatedBy"
            horizontal
            :label="$t('updatedBy')"
            label-for="updatedBy"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
            class="mb-0"
          >
            <div class="form-control-plaintext">
              {{ localOffer.updatedBy.firstname }} {{ localOffer.updatedBy.lastname
              }}<span v-if="localOffer.updatedAt"
                >, {{ $dayjs(localOffer.updatedAt).format('DD.MM.YYYY HH:mm') }}</span
              >
            </div>
          </b-form-group>
        </b-col>
      </b-row>
    </fieldset>

    <b-row>
      <b-col lg="6" md="6" sm="12">
        <AttachmentsSection
          :parentId="localOffer._id"
          :attachments="localOffer.attachments"
          :attachmentTypes="attachmentTypes"
          :isReadOnly="isReadOnly"
          :uploadCallback="uploadAttachment"
          uploadActionType="uploadOfferAttachment"
          @delete-attachment="onDeleteAttachment"
        />
      </b-col>
    </b-row>

    <!-- Form actions in the block project-offers; the view saves through its button bar -->
    <b-row v-if="projectContext">
      <b-col lg="12" md="12" sm="12">
        <div class="form-actions d-flex justify-content-end mt-3">
          <button class="btn btn-default mr-2" @click="$emit('cancel')">
            {{ $t('cancel') }}
          </button>
          <button v-if="!isReadOnly" class="btn btn-inverse" @click="handleSave">
            {{ isNew ? $t('create') : $t('save') }}
          </button>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Multiselect from 'vue-multiselect';
import Calendar from 'primevue/calendar';
import {
  getOfferCustomerStateColor,
  getOfferEditingStateColor,
  getOfferTypeColor,
} from '@/helpers/colors';
import AttachmentsSection from '@/components/AttachmentsSection.vue';
import MultiselectProjects from '@/components/MultiselectProjects.vue';
import MultiselectSuppliers from '@/components/MultiselectSuppliers.vue';
import TaxAmountFields from '@/components/Accounting/TaxAmountFields.vue';

const OFFER_TYPE_INCOMING = 'INCOMING';
const OFFER_ATTACHMENT_TYPES = ['OFFER_DOCUMENT'];

/**
 * Offer of the view offers (see feature page angebote): the project is optional, as on invoices;
 * a supplier only on incoming offers. Tax and gross are shown as calculated, the server
 * calculates them again. In the block project-offers (`projectContext`) the project is fixed and
 * the form saves through its own buttons (`save` / `cancel`).
 */
export default {
  name: 'OfferForm',
  components: {
    Multiselect,
    Calendar,
    AttachmentsSection,
    MultiselectProjects,
    MultiselectSuppliers,
    TaxAmountFields,
  },
  props: {
    offer: {
      type: Object,
      required: true,
    },
    /** Viewer on the view offers: shows the offer and its files, loads no selection lists. */
    isReadOnly: {
      type: Boolean,
      default: false,
    },
    /** Project of the block project-offers; null in the view offers. */
    projectContext: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      localOffer: { ...this.offer },
      attachmentTypes: OFFER_ATTACHMENT_TYPES,
    };
  },
  computed: {
    ...mapGetters(['getEnumValues']),

    /** Changing to outgoing clears the supplier: it is allowed on incoming offers only. */
    offerType: {
      get() {
        return this.localOffer.offerType;
      },
      set(offerType) {
        const supplier = offerType === OFFER_TYPE_INCOMING ? this.localOffer.supplier : null;
        this.localOffer = { ...this.localOffer, offerType, supplier };
      },
    },

    isIncoming() {
      return this.localOffer.offerType === OFFER_TYPE_INCOMING;
    },

    isNew() {
      return !this.localOffer.number || this.localOffer.number === '_new';
    },

    offerDate: {
      get() {
        return this.toDate(this.localOffer.offerDate);
      },
      set(value) {
        this.localOffer.offerDate = value;
      },
    },

    validUntil: {
      get() {
        return this.toDate(this.localOffer.validUntil);
      },
      set(value) {
        this.localOffer.validUntil = value;
      },
    },

    /** Number of the block's project; the attachment paths of the block need it. */
    projectNumber() {
      return this.projectContext ? this.projectContext.number : null;
    },

    offerAmounts() {
      const { netAmount, vatPercentage, taxAmount, grossAmount } = this.localOffer;
      return { netAmount, vatPercentage, taxAmount, grossAmount };
    },
  },
  watch: {
    offer: {
      handler(newOffer) {
        this.localOffer = { ...newOffer };
      },
      deep: true,
    },
    localOffer: {
      handler() {
        this.$emit('update:offer', this.localOffer);
      },
      deep: true,
    },
    projectContext: {
      handler(project) {
        if (project) {
          this.localOffer = { ...this.localOffer, project };
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions(['fetchEnumValues', 'uploadOfferAttachment', 'deleteOfferAttachment']),

    getOfferTypeColor,
    getOfferEditingStateColor,
    getOfferCustomerStateColor,

    toDate(value) {
      return value ? this.$dayjs(value).toDate() : null;
    },

    onAmountsChange(amounts) {
      this.localOffer = { ...this.localOffer, ...amounts };
    },

    handleSave() {
      this.$emit('save', this.localOffer);
    },

    /** Upload of AttachmentsSection; in the block through the project's offer. */
    uploadAttachment(upload) {
      return this.uploadOfferAttachment({
        ...upload,
        offerNumber: this.localOffer.number,
        projectNumber: this.projectNumber,
      });
    },

    async onDeleteAttachment(attachmentId) {
      await this.deleteOfferAttachment({
        offerId: this.localOffer._id,
        offerNumber: this.localOffer.number,
        attachmentId,
        projectNumber: this.projectNumber,
      });
    },
  },
  async mounted() {
    await Promise.all(
      ['OfferType', 'OfferEditingState', 'OfferCustomerState'].map((enumType) =>
        this.fetchEnumValues(enumType)
      )
    );
  },
};
</script>

<style scoped lang="scss">
.p-calendar {
  width: 100%;
}

.offer-fieldset {
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}
</style>
